import { idSchema } from '@erp/shared';

const QUESTIONNAIRE_CUSTOMER_MODE_KEY = 'erp-questionnaire-customer-mode';

export interface QuestionnaireCustomerModeMarker {
  readonly runId: string;
  readonly customerPath: string;
}

function markerFor(runId: string): QuestionnaireCustomerModeMarker | null {
  if (!idSchema.safeParse(runId).success) return null;
  return { runId, customerPath: `/field/runs/${encodeURIComponent(runId)}/customer` };
}

export function readQuestionnaireCustomerModeMarker(): QuestionnaireCustomerModeMarker | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(QUESTIONNAIRE_CUSTOMER_MODE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || !('runId' in parsed)) return null;
    const runId = Reflect.get(parsed, 'runId');
    if (typeof runId !== 'string') return null;
    const expected = markerFor(runId);
    if (
      expected === null ||
      !('customerPath' in parsed) ||
      Reflect.get(parsed, 'customerPath') !== expected.customerPath
    ) {
      return null;
    }
    return expected;
  } catch {
    return null;
  }
}

export function setQuestionnaireCustomerModeMarker(runId: string): QuestionnaireCustomerModeMarker {
  const marker = markerFor(runId);
  if (marker === null) throw new Error('QUESTIONNAIRE_CUSTOMER_MODE_INVALID_RUN');
  window.sessionStorage.setItem(QUESTIONNAIRE_CUSTOMER_MODE_KEY, JSON.stringify(marker));
  return marker;
}

export function clearQuestionnaireCustomerModeMarker(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(QUESTIONNAIRE_CUSTOMER_MODE_KEY);
  } catch {
    // Privacy routing is best effort. Server-side customer-session authorization remains authoritative.
  }
}

export function redirectToQuestionnaireCustomerMode(): boolean {
  const marker = readQuestionnaireCustomerModeMarker();
  if (marker === null || window.location.pathname === marker.customerPath) return false;
  window.location.replace(marker.customerPath);
  return true;
}
